import { Heading } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { FieldConflict } from '@complan/contracts';
import { AriaModalDialog } from '@/shared/a11y/AriaDialog';
import { Button } from '@/components/ui/button';

/**
 * The version-conflict dialog (task 23.2, R5.8).
 *
 * When a `PUT` is rejected with a `409` version mismatch, the server returns the
 * per-field diff of stored versus submitted values. This dialog renders that diff
 * so the user can see what changed under them — and it **keeps the user's entered
 * values**: it never writes back to the form, so dismissing it leaves the form
 * exactly as the user left it, ready to resubmit against the new version. There
 * is no automatic retry (R5.8): reconciling and resubmitting is the user's
 * decision.
 *
 * It is a true dialog (focus trap, focus restore, `Esc`) via
 * {@link AriaModalDialog}.
 */
export interface ConflictDialogProps {
  readonly isOpen: boolean;
  /** The per-field stored-vs-submitted diff from the `409` (R5.8). */
  readonly fields: readonly FieldConflict[];
  readonly storedVersion: number;
  readonly submittedVersion: number;
  /** Dismiss; the form values are untouched, so this is a pure close. */
  readonly onDismiss: () => void;
}

export function ConflictDialog({
  isOpen,
  fields,
  storedVersion,
  submittedVersion,
  onDismiss,
}: ConflictDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <AriaModalDialog
      variant="centre"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onDismiss();
      }}
      aria-label={t('validationSummary.conflict.title')}
    >
      <Heading slot="title" className="text-[15px] font-semibold text-txt">
        {t('validationSummary.conflict.title')}
      </Heading>
      <p className="text-muted-foreground mt-2 text-[12.3px]">
        {t('validationSummary.conflict.body', { storedVersion, submittedVersion })}
      </p>
      <p className="text-muted-foreground mt-1 text-[11.5px]">
        {t('validationSummary.conflict.keepNote')}
      </p>

      {fields.length > 0 ? (
        <table className="mt-3 w-full border-collapse text-[12px]">
          <thead>
            <tr className="text-left text-txt">
              <th className="border-line border-b py-1 pr-2 font-semibold">
                {t('validationSummary.conflict.colField')}
              </th>
              <th className="border-line border-b py-1 pr-2 font-semibold">
                {t('validationSummary.conflict.colStored')}
              </th>
              <th className="border-line border-b py-1 font-semibold">
                {t('validationSummary.conflict.colSubmitted')}
              </th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field.path}>
                <td className="border-line border-b py-1 pr-2 align-top font-medium">
                  {t(fieldLabelKey(field.path), { defaultValue: field.path })}
                </td>
                <td className="border-line border-b py-1 pr-2 align-top text-[#8E2A1E]">
                  {formatConflictValue(field.storedValue)}
                </td>
                <td className="border-line border-b py-1 align-top text-[#14538F]">
                  {formatConflictValue(field.submittedValue)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="mt-3 text-[12px] text-txt">{t('validationSummary.conflict.noFieldDiff')}</p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" onClick={onDismiss}>
          {t('validationSummary.conflict.dismiss')}
        </Button>
      </div>
    </AriaModalDialog>
  );
}

/**
 * Render one diff value for display. The diff carries stored and submitted values
 * as opaque JSON; this renders scalars directly and shows a compact JSON form for
 * objects and arrays, so a structured field (a Platform set, a date pair) is still
 * legible without a per-field renderer. `null`/`undefined` render as the catalogue
 * empty marker, resolved by the caller — here we return a sentinel the cell wraps.
 */
function formatConflictValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length > 0 ? value : '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** The catalogue key for a conflicting field's label; shared with the summary. */
function fieldLabelKey(path: string): string {
  return `validationSummary.field.${path.replace(/\./g, '_')}`;
}
